import { FilterMode } from "@/shared/models/filters";
import { InformationCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useDismissibleHint } from "@/shared/hooks/useDismissibleHint";
import clsx from "clsx";
import { Link } from "react-router-dom";
import { usePortalContainer } from "@/shared/context/PortalContext/usePortalContext";

interface FilterModeSelectorProps {
  filterMode: FilterMode;
  onFilterModeChange: (mode: FilterMode) => void;
  disabled?: boolean;
}

export const FilterModeSelector = ({
  filterMode,
  onFilterModeChange,
  disabled,
}: FilterModeSelectorProps) => {
  return (
    <div
      className={clsx(
        "space-y-2 mt-2",
        disabled && "opacity-50 pointer-events-none"
      )}
    >
      <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">
        Combine by
      </div>
      <div className="flex gap-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onFilterModeChange("OR");
          }}
          className={`flex-1 px-2.5 py-1.5 text-[11px] font-medium rounded border transition-colors ${
            filterMode === "OR"
              ? "border-squawk-blue bg-squawk-blue/20 text-white"
              : "border-gray-700 bg-gray-800/50 text-gray-400 hover:border-gray-600"
          }`}
        >
          Any
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onFilterModeChange("AND");
          }}
          className={`flex-1 px-2.5 py-1.5 text-[11px] font-medium rounded border transition-colors ${
            filterMode === "AND"
              ? "border-squawk-blue bg-squawk-blue/20 text-white"
              : "border-gray-700 bg-gray-800/50 text-gray-400 hover:border-gray-600"
          }`}
        >
          All
        </button>
      </div>
    </div>
  );
};

interface CreateNewLinkProps {
  url: string;
  label?: string;
}

export const CreateNewLink = ({ url, label = "Create new..." }: CreateNewLinkProps) => {
  const portalContainer = usePortalContainer();

  // Don't show this link in popout windows - creation needs full screen context
  const isPopout = portalContainer !== null;
  if (isPopout) {
    return null;
  }

  return (
    <Link
      to={url}
      onClick={(e) => e.stopPropagation()}
      className="text-[10px] text-squawk-blue hover:text-squawk-blue-dark transition-colors"
    >
      {label}
    </Link>
  );
};

export const ManageFiltersButton = () => {
  const portalContainer = usePortalContainer();

  // Don't show this button in popout windows - filter management needs full screen context
  const isPopout = portalContainer !== null;
  if (isPopout) {
    return null;
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Use window.location for navigation to avoid router dependency
    window.location.href = "/watchlists-filters";
  };

  return (
    <div className="space-y-2 mt-2">
      <button
        onClick={handleClick}
        className="w-full px-2.5 py-1.5 text-[11px] font-medium rounded border border-gray-700 bg-gray-800/50 text-gray-400 hover:border-squawk-blue hover:text-squawk-blue transition-colors"
      >
        Manage combined watchlists
      </button>
    </div>
  );
};

interface ClearSelectionsFooterProps {
  clearSelections?: () => void;
  disableClear?: boolean;
}

export const ClearSelectionsFooter = ({
  clearSelections,
  disableClear,
}: ClearSelectionsFooterProps) => {
  // Don't render anything if there are no selections to clear
  if (disableClear) {
    return null;
  }

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        clearSelections?.();
      }}
      className="w-full px-2.5 py-1.5 text-[11px] font-medium rounded border border-red-600/50 bg-red-600/20 text-red-400 hover:border-red-500 hover:bg-red-600/30 hover:text-red-300 transition-colors"
    >
      Clear All Selections
    </button>
  );
};

/** Which selection keys each filtering mechanism owns. */
export const FILTER_OWNERS = {
  lists: [
    "portfolios-filter",
    "portfolios-filter-prebuilt",
    "ticker-watchlists-filter",
    "keyword-watchlists-filter",
    // Tag watchlists belong here like any other list. Leaving them out made
    // the exclusion one-way — a tag watchlist did not count as "filtering by
    // lists", so a combined watchlist stayed selectable beside it, which is
    // the collision the rule exists to prevent — and the note's Clear button
    // silently skipped them.
    "tag-watchlists-filter",
    "basic-filter",
  ],
  views: ["category-filter"],
} as const;

export type FilterOwner = keyof typeof FILTER_OWNERS;

interface FilterConflictNoteProps {
  /** The mechanism currently owning the Filter column. */
  owner: FilterOwner;
  /** Name of the active combined watchlist, when one owns the column. */
  activeName?: string;
  /** Id of that watchlist, for the deep link into its editor. */
  activeId?: string;
}

/**
 * How selecting watchlists works, said once.
 *
 * This was an amber box reading "Filtering by individual lists." with a Clear
 * button — permanent, coloured like a warning, and describing a *state* rather
 * than the rule behind it. It looked like something had gone wrong, so it got
 * read as an error and ignored as noise at the same time.
 *
 * Now it states the rule, positively, in the panel's ordinary colours, and can
 * be closed for good. The rule is worth explaining once; explaining it on
 * every visit forever is nagging.
 *
 * Dismissing it is safe because it is not the only explanation: the rows this
 * affects carry the same reason in their title, so "why is that grey?" is
 * always answerable at the moment it is asked, at no visual cost.
 */
export const FilterConflictNote = ({
  owner,
  activeName,
  activeId,
}: FilterConflictNoteProps) => {
  const { dismissed, dismiss } = useDismissibleHint("watchlist-selection");
  if (dismissed) return null;

  return (
    <div className="flex items-start gap-2 rounded border border-gray-700 bg-gray-800/70 px-2.5 py-2">
      <InformationCircleIcon className="mt-px size-4 shrink-0 text-gray-500" />
      <p className="flex-1 text-xs leading-relaxed text-gray-300">
        Select as many watchlists as you like. A combined watchlist that uses
        AND filters on its own.
        {owner === "views" && (
          <>
            {" "}
            <a
              href={`/watchlists-filters${activeId ? `?editFilter=${activeId}` : ""}`}
              title={
                activeName
                  ? `Add more lists to \u201C${activeName}\u201D`
                  : "Edit this watchlist"
              }
              className="text-squawk-blue underline underline-offset-2 hover:text-squawk-blue/80"
            >
              Add more to it
            </a>
          </>
        )}
      </p>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Got it"
        className="shrink-0 cursor-pointer border-none bg-transparent p-0.5 text-gray-500 transition-colors hover:text-gray-300"
      >
        <XMarkIcon className="size-3.5" />
      </button>
    </div>
  );
};
